import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LoanCard } from "@/components/LoanCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddLoanDialog } from "@/components/AddLoanDialog";
import { AddClientDialog } from "@/components/AddClientDialog";
import { AppShell } from "@/components/layout/AppShell";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { PortfolioHealthPanel } from "@/components/dashboard/PortfolioHealthPanel";
import { UpcomingDuesPanel } from "@/components/dashboard/UpcomingDuesPanel";
import { DollarSign, AlertCircle, TrendingUp, Search, Percent, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { isPast } from "date-fns";
import { calculateClientScore } from "@/lib/clientScore";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { useCurrency } from "@/hooks/useUserCurrency";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"all" | "overdue">("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { hasActiveSubscription, isLoading: subLoading } = useSubscriptionLimits(user?.id);
  const { format } = useCurrency();

  useEffect(() => {
    if (!user || subLoading) return;
    if (!hasActiveSubscription) {
      toast.error("Sua assinatura expirou. Assine o plano PRO para continuar.");
      navigate("/subscription");
    }
  }, [user, hasActiveSubscription, subLoading, navigate]);

  useEffect(() => {
    let responded = false;

    const finishLoading = () => {
      if (!responded) {
        responded = true;
        clearTimeout(safetyTimeout);
        setIsLoading(false);
      }
    };

    // Safety timeout: if neither auth path resolves, force isLoading false
    // and retry getSession() automatically to try to unblock the client.
    const safetyTimeout = setTimeout(() => {
      if (!responded) {
        console.warn('Auth session check timed out; forcing loading off and retrying...');
        finishLoading();
        supabase.auth.getSession().then(({ data: { session }, error }) => {
          if (error) {
            console.error('Retry session check error:', error);
            navigate('/auth');
            return;
          }
          if (session?.user) {
            setUser(session.user);
          } else {
            navigate('/auth');
          }
        });
      }
    }, 8000);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      
      if (event === 'SIGNED_OUT') {
        setUser(null);
        finishLoading();
        navigate('/auth');
        return;
      }
      
      if (session?.user) {
        setUser(session.user);
        finishLoading();
      } else if (event !== 'INITIAL_SESSION') {
        setUser(null);
        finishLoading();
        navigate('/auth');
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session check error:', error);
        finishLoading();
        navigate('/auth');
        return;
      }
      
      if (session?.user) {
        setUser(session.user);
      } else {
        navigate('/auth');
      }
      finishLoading();
    });

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === "/clientes") {
      const timer = setTimeout(() => {
        const el = document.getElementById("clientes");
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    if (location.pathname === "/vencimentos") {
      setFilterStatus("overdue");
    }
  }, [location.pathname]);

  const { data: loansData = [], refetch, isLoading: loansLoading, isError: loansError } = useQuery({
    queryKey: ["loans", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loans")
        .select(`
          *,
          clients!loans_client_id_fkey(name, phone, cpf)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

const parseLocalDate = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const loans = loansData.map((loan: any) => ({
  id: loan.id,
  clientId: loan.client_id,
  clientName: loan.clients?.name ?? "Cliente não encontrado",
  amount: Number(loan.amount),
  dueDate: parseLocalDate(loan.due_date),
  loanDate: parseLocalDate(loan.loan_date),
  status: loan.status as "paid" | "active" | "overdue",
  interestRate: Number(loan.interest_rate),
  interestType: loan.interest_type as "monthly" | "fixed" | "daily",
  installments: loan.installments || 1,
  paidInstallments: loan.paid_installments || 0,
  dailyPenalty: Number((loan as any).daily_penalty ?? 0),
  installmentFrequency: ((loan as any).installment_frequency as "weekly" | "biweekly" | "monthly") || "monthly",
  installmentValues: (loan as any).installment_values ?? null,
  phone: loan.clients?.phone ?? null,
  cpf: loan.clients?.cpf ?? null,
  updatedAt: new Date(loan.updated_at),
}));

  // Carteira em aberto (exclui quitados)
  const openLoans = loans.filter((loan) => loan.status !== "paid");

  // Filter by search term - show only overdue and active loans
  const searchFilteredLoans = searchTerm.trim() 
    ? loans.filter((loan) => {
        const isOverdueOrActive = loan.status === "overdue" || loan.status === "active";
        return isOverdueOrActive && loan.clientName.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : loans;

  const overdueLoans = searchFilteredLoans.filter((loan) => loan.status === "overdue" || (loan.status === "active" && isPast(loan.dueDate)));
  const totalLoaned = openLoans.reduce((sum, loan) => sum + loan.amount, 0);
  const totalInterest = searchFilteredLoans.reduce((sum, loan) => {
    if (loan.interestType === "monthly" && loan.interestRate > 0) {
      return sum + (loan.amount * loan.interestRate / 100);
    }
    return sum;
  }, 0);
  
  // Query payments to get actual received interest in current month
  const { data: paymentsData = [] } = useQuery({
    queryKey: ["payments", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const currentDate = new Date();
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      
      const { data, error } = await supabase
        .from("loan_payments")
        .select("interest_amount")
        .eq("user_id", user.id)
        .gte("paid_at", firstDayOfMonth.toISOString())
        .lte("paid_at", lastDayOfMonth.toISOString());

      if (error) throw error;
      return data;
    },
  });

  const receivedInterest = paymentsData.reduce((sum, payment) => sum + Number(payment.interest_amount), 0);

  // Todos pagamentos (para cálculo de score)
  const { data: allPaymentsData = [] } = useQuery({
    queryKey: ["all-payments-score", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loan_payments")
        .select("loan_id, paid_at, interest_amount")
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
  });

  const scoreLoans = loans.map((l) => ({
    id: l.id,
    clientId: l.clientId,
    amount: l.amount,
    dueDate: l.dueDate,
    loanDate: l.loanDate,
    status: l.status,
    interestType: l.interestType,
  }));
const scorePayments = (allPaymentsData as any[]).map((p) => ({
  loanId: p.loan_id,
  paidAt: new Date(p.paid_at),
  interestAmount: Number(p.interest_amount),
}));

const clientScoreMap = useMemo(() => {
  const map = new Map<string, ReturnType<typeof calculateClientScore>>();
  const uniqueClientIds = new Set(scoreLoans.map((l) => l.clientId));
  uniqueClientIds.forEach((clientId) => {
    map.set(clientId, calculateClientScore(clientId, scoreLoans, scorePayments));
  });
  return map;
}, [loansData, allPaymentsData]);
  
  const overdueCount = overdueLoans.length;
  const activeCount = searchFilteredLoans.filter((loan) => loan.status === "active" && !isPast(loan.dueDate)).length;
  const paidCount = searchFilteredLoans.filter((loan) => loan.status === "paid").length;
  
  // Get unique clients count
  const uniqueClients = new Set(searchFilteredLoans.map(loan => loan.clientName)).size;

  // Oculta empréstimos quitados/removidos da lista principal
  // (eles continuam disponíveis em Remarketing/Central Inteligente).
  const visibleLoans = searchFilteredLoans.filter((loan) => loan.status !== "paid");
  const filteredLoans = filterStatus === "overdue" ? overdueLoans : visibleLoans;

  const handleOverdueClick = () => {
    setFilterStatus(filterStatus === "overdue" ? "all" : "overdue");
  };

  const handleLogout = async () => {
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: "local" }),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
      toast.success("Logout realizado com sucesso!");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/auth");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  // Trend mock (visual only): compare loaned/interest vs previous month from payments data
  const _now = new Date();
  const _monthName = _now.toLocaleDateString("pt-BR", { month: "long" });
  const monthLabel = `${_monthName.charAt(0).toUpperCase()}${_monthName.slice(1)} ${_now.getFullYear()}`;
  const carteiraHealthy = openLoans.length === 0
    ? 100
    : Math.round(
        (openLoans.filter((l) => !isPast(l.dueDate)).length / openLoans.length) * 100,
      );

  return (
    <AppShell
      overdueCount={overdueCount}
      userName={(user?.user_metadata?.full_name as string) || user?.email?.split("@")[0] || "Usuário"}
      userRole="Administrador"
    >
      <div className="px-4 sm:px-5 lg:px-9 py-5 sm:py-6 lg:py-8 pb-8 space-y-5 sm:space-y-6 lg:space-y-7 animate-fade-in">
        {/* Top bar */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="leading-tight">
            <h1 className="text-[22px] sm:text-[26px] lg:text-[30px] font-bold tracking-tight text-foreground">
              Painel financeiro
            </h1>
            <p className="text-[12px] sm:text-[13px] text-muted-foreground mt-1">
              {monthLabel} ·{" "}
              <span className="text-[hsl(159_83%_34%)] font-semibold">
                carteira {carteiraHealthy >= 70 ? "saudável" : carteiraHealthy >= 40 ? "em atenção" : "crítica"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2.5 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-[320px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar cliente, empréstimo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 rounded-xl relative"
              onClick={handleOverdueClick}
              title="Notificações"
            >
              <Bell className="h-4 w-4" />
              {overdueCount > 0 && (
                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[hsl(4_70%_58%)]" />
              )}
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <AddClientDialog onSuccess={refetch} />
              <AddLoanDialog onSuccess={refetch} />
            </div>
          </div>
          <div className="sm:hidden grid grid-cols-2 gap-2 [&_button]:w-full [&_button]:h-11 [&_button]:px-3 [&_button]:text-xs">
            <AddClientDialog onSuccess={refetch} />
            <AddLoanDialog onSuccess={refetch} />
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid gap-3 sm:gap-4 lg:gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
          <DashboardKpiCard
            label="Total emprestado"
            value={format(totalLoaned, { maximumFractionDigits: 0 })}
            icon={DollarSign}
            tone="primary"
            trendValue="12,4%"
            trendPositive
            trendLabel="vs. mês anterior"
          />
          <DashboardKpiCard
            label="Total de juros"
            value={format(totalInterest, { maximumFractionDigits: 0 })}
            icon={Percent}
            tone="warning"
            trendValue="8,1%"
            trendPositive
            trendLabel={`${format(receivedInterest, { maximumFractionDigits: 0 })} no mês`}
          />
          <DashboardKpiCard
            label="Empréstimos ativos"
            value={activeCount}
            icon={TrendingUp}
            tone="success"
            trendValue={`+${activeCount}`}
            trendPositive
            trendLabel="em andamento"
          />
          <DashboardKpiCard
            label="Vencidos"
            value={overdueCount}
            icon={AlertCircle}
            tone="destructive"
            attention="requer atenção"
          />
        </div>

        {/* Painel saúde da carteira + vencimentos próximos */}
        <div className="grid gap-4 sm:gap-5 grid-cols-1 xl:grid-cols-[1.6fr_1fr]">
          <PortfolioHealthPanel
            loans={openLoans}
            payments={(allPaymentsData as any[]).map((p) => ({
              amount: Number(p.interest_amount ?? 0),
              paidAt: new Date(p.paid_at),
            }))}
            onSeeReport={() => navigate("/dashboard")}
          />
          <UpcomingDuesPanel
            loans={openLoans}
            onViewAll={() => setFilterStatus("overdue")}
          />
        </div>

        {/* Loans list */}
        <div id="clientes" className="space-y-4 lg:space-y-5">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                {filterStatus === "overdue" ? "Empréstimos vencidos" : "Clientes recentes"}
              </h2>
              <p className="text-xs text-muted-foreground">
                {filterStatus === "overdue"
                  ? `${overdueCount} empréstimos em atraso`
                  : "Acompanhe o status de cada financiamento"}
              </p>
            </div>
            {filterStatus === "overdue" && (
              <Button variant="outline" onClick={() => setFilterStatus("all")}>
                Ver Todos
              </Button>
            )}
          </div>

          {loansError ? (
            <div className="surface-card p-8 lg:p-12 text-center">
              <p className="text-muted-foreground mb-2 text-base">
                Não foi possível carregar seus empréstimos.
              </p>
              <p className="text-xs text-muted-foreground/70 mb-4">
                Verifique sua conexão e tente novamente.
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : loansLoading ? (
            <div className="surface-card p-8 lg:p-12 text-center">
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-t-2 border-b-2 border-primary" />
              <p className="text-xs text-muted-foreground/70">Carregando seus empréstimos...</p>
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="surface-card p-8 lg:p-12 text-center">
              <p className="text-muted-foreground mb-2 text-base">
                {filterStatus === "overdue"
                  ? "Nenhum empréstimo vencido."
                  : "Nenhum empréstimo cadastrado ainda."}
              </p>
              <p className="text-xs text-muted-foreground/70">
                {filterStatus === "overdue"
                  ? "Todos os empréstimos estão em dia!"
                  : "Comece adicionando um cliente e depois crie um empréstimo."}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4 lg:gap-5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
              {filteredLoans.map((loan, index) => (
                <div key={loan.id} style={{ animationDelay: `${index * 50}ms` }} className="animate-scale-in">
                  <LoanCard
                    {...loan}
                    score={clientScoreMap.get(loan.clientId)}
                    onDelete={refetch}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default Index;
